import React, { useState } from "react";
import { LayoutPanelTop, Repeat, PanelTop, Image, Truck, Boxes } from "lucide-react";
import { IMAGES } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionHeader, CTAButton, Eyebrow, useSeo } from "../components/Shared";

const SECTIONS = [
  { icon: LayoutPanelTop, t: "Custom Exhibits", d: "Design and fabrication for a brand-specific presence built around your goals." },
  { icon: Repeat, t: "Rental Exhibits", d: "Flexible, reconfigurable options suited to changing footprints or event needs." },
  { icon: PanelTop, t: "Portable Displays", d: "Practical solutions such as banners, pop-ups, and modular elements." },
  { icon: Image, t: "Graphics and Collateral", d: "Coordinated exhibit graphics, signage, handouts, and branded materials." },
  { icon: Truck, t: "Logistics and Show Services", d: "Shipping, event forms, labor coordination, drayage support, installation, dismantling, and onsite coordination." },
  { icon: Boxes, t: "Exhibit Storage and Management", d: "Store, inspect, track, prepare, and ship exhibit assets between events." },
];

const FACTORS = [
  { label: "Frequency of use", rent: "Occasional / few shows a year", buy: "Frequent, recurring shows" },
  { label: "Need for flexibility", rent: "Footprints and layouts change often", buy: "Consistent booth requirements" },
  { label: "Ownership preference", rent: "Prefer not to own or maintain", buy: "Want a dedicated brand asset" },
  { label: "Storage", rent: "Limited or no storage plan", buy: "Storage handled (MJG can help)" },
  { label: "Budget & goals", rent: "Lower upfront, per-event spend", buy: "Invest once, reuse over time" },
];

export default function TradeShow() {
  const [tab, setTab] = useState("rent");
  useSeo(
    "Trade Show Exhibits & Booth Rental | MJG Promotions",
    "Custom trade show exhibits, rental booths, and portable displays in New Jersey. Design, graphics, logistics, installation, storage, and exhibit management by MJG."
  );
  return (
    <>
      <PageHero
        eyebrow="Trade Show Exhibits"
        title="Trade Show Exhibits Built Around Your Brand"
        intro="Whether you need a portable display, a flexible rental, or a custom exhibit, MJG brings the design, graphics, logistics, and event support together."
        image={IMAGES.exhibit}
      />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <SectionHeader eyebrow="Exhibit Services" title="Design, Ship, Install, and Manage" />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t} delay={(i % 3) * 0.06}>
                <div className="group h-full rounded-sm border border-brand-line bg-brand-warm p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-red hover:bg-white hover:shadow-xl">
                  <s.icon className="h-7 w-7 text-brand-red" aria-hidden="true" />
                  <h3 className="mt-5 text-lg font-bold text-brand-black">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-gray">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Rent or purchase tool */}
      <section className="bg-brand-black py-20 sm:py-28" data-testid="rent-or-buy">
        <div className="mx-auto max-w-4xl px-5">
          <div className="text-center">
            <Eyebrow className="justify-center">Rent or Purchase?</Eyebrow>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">Find the Right Fit for Your Program</h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-white/65">A quick, neutral comparison based on how you exhibit. This is general guidance — talk to us about your specific program.</p>
          </div>

          <div className="mt-10 flex justify-center gap-2">
            {[["rent", "Consider Renting"], ["buy", "Consider Buying"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`rentbuy-tab-${k}`}
                onClick={() => setTab(k)}
                className={`px-5 py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-brand-red text-white" : "border border-white/20 text-white/70 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-8 divide-y divide-white/10 border border-white/10">
            {FACTORS.map((f) => (
              <div key={f.label} className="grid grid-cols-3 gap-4 p-4 text-sm">
                <span className="font-semibold text-white">{f.label}</span>
                <span className="col-span-2 text-white/70">{tab === "rent" ? f.rent : f.buy}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <CTAButton to="/contact" testId="tradeshow-page-cta" variant="primary">Plan Your Next Exhibit</CTAButton>
          </div>
        </div>
      </section>
    </>
  );
}
